import { z } from 'zod';
import { camelCase, kebabCase, zipObject } from 'lodash';
import type { HTMLAttributes } from 'react';
import { Locale, Segment } from '@fixter/i18n';
import type { Writable } from 'type-fest';
import type { CmsCollectionItems } from '$cms/types';
import {
  CmsAreaItemSchema,
  CmsCarMakeItemSchema,
  CmsCarModelItemSchema,
  CmsProductItemSchema,
  CmsShortTextSchema,
} from '$cms/types';
import type { ContentComponent, ContentComponentFetchServerSideData } from '$cms/ContentComponent';
import type { SchemaType } from '$util/schema';
import { useAppContext } from '$util/AppContext';
import { fetchAreaCollectionsData } from '$server/modules/cms/fetchAreaCollectionData';
import { fetchCarMakeCollectionData } from '$server/modules/cms/fetchCarMakeCollectionData';
import { fetchCarModelCollectionData } from '$server/modules/cms/fetchCarModelCollectionData';
import { fetchProductCollectionData } from '$server/modules/cms/fetchProductCollectionData';
import { Col, Row } from '$src-components/atoms/Grid';
import type { AppContextGlobal } from '$util/AppContextGlobal';
import { useCmsPageContext } from '$cms/CmsPageContext';
import { Link } from '$quantum/atoms/Link';
import { ContentComponentSection } from '@/components/atoms/ContentComponentSection';
import type { ILink } from '@/components/atoms/SimpleLink';
import { Card } from '@/components/atoms/Card';
import { HeadingLevels } from '@/components/atoms/HeadingLevels';
import { SimpleLink } from '@/components/atoms/SimpleLink';
import type { ServerLogger } from '@/server/logger';
import {
  makePopularAreaLink,
  makePopularMakeLink,
  makePopularModelLink,
  makePopularProductLink,
} from './utils/makePopularLinks';
import { makePopularLinksListHeading } from './utils/makePopularLinksListHeading';
import { getLinksListTitle } from './utils/getLinksListsTitle';
import { makeViewAllLink } from './utils/makeViewAllLink';

import styles from './index.module.scss';

export interface PopularLinksListProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const PopularLinksListSchema = {
  translatedEntities: z.object({
    title: CmsShortTextSchema.optional(),
  }),
  configuration: z
    .object({
      layout: z.string().array(),
    })
    .optional(),
};

export type PopularLinksListTranslatedEntities = SchemaType<
  (typeof PopularLinksListSchema)['translatedEntities']
>;
export type PopularLinksListConfiguration = SchemaType<(typeof PopularLinksListSchema)['configuration']>;

const PopularLinksListServerSideDataSchema = z.object({
  segment: z.string(),
  topCollectionItems: z.object({
    area: CmsAreaItemSchema.pick({ name: true, slug: true }).array().optional(),
    product: CmsProductItemSchema.pick({ name: true, slug: true }).array().optional(),
    carMake: CmsCarMakeItemSchema.pick({ name: true, slug: true }).array().optional(),
    carModel: CmsCarModelItemSchema.pick({ name: true, slug: true, make: true }).array().optional(),
  }),
  popularAreas: z
    .object({
      areaName: z.string(),
      slug: z.string(),
    })
    .array(),
});

export type PopularLinksListServerSideData = SchemaType<typeof PopularLinksListServerSideDataSchema>;

export const PopularLinksList: ContentComponent<
  PopularLinksListTranslatedEntities,
  PopularLinksListConfiguration,
  PopularLinksListProps,
  PopularLinksListServerSideData
> = function PopularLinksList({
  translatedEntities,
  configuration,
  serverSideData = { segment: Segment.Fixter_UK, topCollectionItems: {} },
  className,
  ...props
}): JSX.Element {
  const cmsPageContext = useCmsPageContext();
  const pageCollectionItems = cmsPageContext?.pageRoutes?.[0]?.collectionItems ?? {};
  const { segment, topCollectionItems = {}, popularAreas = [] } = serverSideData;
  const confLayout = configuration?.layout || [
    'area,product',
    `${topCollectionItems?.carModel ? 'car-model' : 'car-make'}:3`,
  ];
  const AppContext = useAppContext<AppContextGlobal>();
  const { locale } = AppContext;

  // @NOTE Hack show all links
  const showAllLinks = locale === Locale.EN_GB;

  return (
    <ContentComponentSection>
      <Row className={styles.PopularLinksListWrapper}>
        <Col>
          <Card className={styles.PopularLinksListContainer} cornerRadius={4} {...props}>
            <HeadingLevels className={styles.heading} semanticLevel={3} styleLevel={2}>
              {translatedEntities.title?.value ||
                makePopularLinksListHeading(pageCollectionItems, segment as Segment)}
            </HeadingLevels>
            {confLayout.map((rows) => {
              const rowLayoutConfig = rows.split(',');
              return (
                <div className={styles.LinksRow} key={rows}>
                  {rowLayoutConfig.map((layout) => {
                    /**
                     * @NOTE a layout may contain a column specification
                     * example "car-make:3", which displays the car-make section in 3 columns
                     */
                    const [sectionName, columns] = layout.trim().split(':');
                    const showColumns = +columns || 1;

                    const section = camelCase(sectionName) as keyof typeof topCollectionItems;

                    const links: ILink[] = [];
                    const viewAllLink = makeViewAllLink(section, segment as Segment);

                    switch (section) {
                      case 'product': {
                        topCollectionItems[section]?.map((item) =>
                          links.push(makePopularProductLink(pageCollectionItems, item, segment as Segment))
                        );
                        break;
                      }
                      case 'area':
                        if (segment === Segment.Fixter_UK && !pageCollectionItems.product) {
                          popularAreas.forEach(({ areaName, slug }) => {
                            links.push(
                              makePopularAreaLink(
                                pageCollectionItems,
                                { name: areaName, slug },
                                segment as Segment
                              )
                            );
                          });
                        } else {
                          topCollectionItems[section]?.map((item) =>
                            links.push(makePopularAreaLink(pageCollectionItems, item, segment as Segment))
                          );
                        }
                        break;
                      case 'carMake':
                        topCollectionItems[section]?.map((item) =>
                          links.push(makePopularMakeLink(pageCollectionItems, item, segment as Segment))
                        );
                        break;
                      case 'carModel':
                        topCollectionItems[section]?.map((item) =>
                          links.push(makePopularModelLink(pageCollectionItems, item))
                        );
                        break;
                      default:
                        // do nothing
                        break;
                    }

                    if (links.length) {
                      return (
                        <Card
                          key={sectionName}
                          className={styles.linksCard}
                          cornerRadius={1}
                          style={{
                            // @ts-ignore Pass prop to styles
                            '--split': rowLayoutConfig.length,
                          }}
                        >
                          <HeadingLevels semanticLevel={4} styleLevel={6}>
                            {getLinksListTitle(section, segment as Segment)}
                          </HeadingLevels>
                          <ol
                            className={styles.linksList}
                            style={{
                              // @ts-ignore Pass prop to styles
                              '--columns': showColumns,
                            }}
                          >
                            {links.map(({ name, link, type = 3 }) => (
                              <li key={kebabCase(name)}>
                                <SimpleLink href={link} text={name} type={type} />
                              </li>
                            ))}
                          </ol>
                          {showAllLinks && viewAllLink && (
                            <Link
                              className={styles.viewAllLink}
                              type="M"
                              href={viewAllLink.link}
                              iconId="arrows/arrow-up-right"
                              purpleIcon
                            >
                              {viewAllLink.name}
                            </Link>
                          )}
                        </Card>
                      );
                    }

                    return null;
                  })}
                </div>
              );
            })}
          </Card>
        </Col>
      </Row>
    </ContentComponentSection>
  );
};

PopularLinksList.displayName = 'PopularLinksList';
PopularLinksList.schema = PopularLinksListSchema;

export const fetchServerSideData: ContentComponentFetchServerSideData<
  PopularLinksListServerSideData
> = async ({ pageRoutes, segment }, preview, container, req) => {
  const logger = container.get<ServerLogger>('dynamicRouteLogger');
  const log = logger
    .traceRequest(req)
    .child({ label: `${PopularLinksList.displayName}.fetchServerSideData` });

  log.info('Fetching popular links data');

  const popularAreas = container.get<any>('PopularGarageAreas');

  const queryContext = { headers: req.headers };

  const topCollectionKeys = ['area', 'product'];
  const topCollectionValues: Promise<CmsCollectionItems[]>[] = [
    fetchAreaCollectionsData(
      {
        filter: {
          key: 'popularity',
          operator: 'lte',
          value: 10,
        },
        limit: 20,
        segment,
        preview,
      },
      queryContext
    ),
    fetchProductCollectionData(
      {
        filter: {
          key: 'popularity',
          operator: 'lte',
          value: 10,
        },
        limit: 20,
        segment,
        preview,
      },
      queryContext
    ),
  ];

  const collectionItems = pageRoutes?.[0]?.collectionItems;
  if (collectionItems && 'carMake' in collectionItems && collectionItems.carMake) {
    topCollectionKeys.push('carModel');
    topCollectionValues.push(
      fetchCarModelCollectionData(
        {
          filter: {
            key: 'make.id',
            operator: 'eq',
            value: collectionItems?.carMake?.id,
          },
          limit: 20,
          preview,
        },
        queryContext
      )
    );
  } else {
    topCollectionKeys.push('carMake');
    topCollectionValues.push(
      fetchCarMakeCollectionData(
        {
          filter: {
            key: 'popularity',
            operator: 'lte',
            value: 100,
          },
          limit: 20,
          segment,
          preview,
        },
        queryContext
      )
    );
  }

  const topCollectionItems = zipObject(topCollectionKeys, await Promise.all(topCollectionValues));

  const res: Writable<PopularLinksListServerSideData> = {
    topCollectionItems,
    segment,
    popularAreas,
  };

  return PopularLinksListServerSideDataSchema.parse(res);
};
